import { useEffect, useState } from 'react'
import { ReactComponent as TikTokIcon } from 'i/icons/social_media/logo-tiktok.svg'
import { ReactComponent as YoutubeIcon } from 'i/icons/social_media/youtube.svg'
import { ReactComponent as LinkedInIcon } from 'i/icons/social_media/logo-linkedin.svg'
import { ReactComponent as InstagramIcon } from 'i/icons/social_media/logo-instagram.svg'
import { ReactComponent as FacebookIcon } from 'i/icons/social_media/logo-fb-simple.svg'
import { ReactComponent as TwitterIcon } from 'i/icons/social_media/logo-twitter.svg'
import { ReactComponent as ClockIcon } from 'i/lock.svg'
import { ReactComponent as LockIcon } from 'i/award.svg'
import sessionTypes from 'utils/sessionTypes.json'

function ProfileSideBar({ profile, eventEntryFee }) {
  const [isBioDrawerOpen, setIsBioDrawerOpen] = useState(false)

  useEffect(() => {
    const onEscClose = (event) => {
      if (event.key === 'Escape') {
        setIsBioDrawerOpen(false)
      }
    }

    if (isBioDrawerOpen) {
      document.addEventListener('keydown', onEscClose)
    }

    return () => {
      document.removeEventListener('keydown', onEscClose)
    }
  }, [isBioDrawerOpen])

  if (!profile) return null

  const getFieldValue = (...keys) => {
    for (const key of keys) {
      const topLevelValue = profile?.[key]
      if (topLevelValue) return topLevelValue

      const nestedUserValue = profile?.user?.[key]
      if (nestedUserValue) return nestedUserValue
    }
    return ''
  }

  const normalizeHandle = (value, platform) => {
    if (!value) return ''

    let normalized = String(value).trim()
    if (!normalized) return ''

    normalized = normalized.replace(/^https?:\/\//i, '').replace(/^www\./i, '')

    const slashIndex = normalized.indexOf('/')
    const host = slashIndex >= 0 ? normalized.slice(0, slashIndex).toLowerCase() : ''
    const path = slashIndex >= 0 ? normalized.slice(slashIndex + 1) : normalized
    const segments = path.split('/').filter(Boolean)

    if (platform === 'linkedin' && host.includes('linkedin.com')) {
      const inIndex = segments.indexOf('in')
      normalized = inIndex >= 0 ? segments[inIndex + 1] || '' : segments[0] || ''
    } else if (platform === 'instagram' && host.includes('instagram.com')) {
      normalized = segments[0] || ''
    } else if (platform === 'facebook' && host.includes('facebook.com')) {
      normalized = segments[0] || ''
    } else if (
      platform === 'twitter' &&
      (host.includes('twitter.com') || host.includes('x.com'))
    ) {
      normalized = segments[0] || ''
    } else if (platform === 'tiktok' && host.includes('tiktok.com')) {
      const segment = segments[0] || ''
      normalized = segment.startsWith('@') ? segment.slice(1) : segment
    }

    return normalized.replace(/^@+/, '').trim()
  }

  const normalizeWebsiteUrl = (value) => {
    if (!value) return ''
    const parsed = String(value).trim()
    if (!parsed) return ''
    return /^https?:\/\//i.test(parsed) ? parsed : `https://${parsed}`
  }

  const domainFromUrl = (url) => {
    let result
    let match
    if ((match = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?=]+)/im))) {
      result = match[1]
      if ((match = result.match(/^[^.]+\.(.+\..+)$/))) {
        result = match[1]
      }
    }
    return result
  }

  const bioWords = profile?.bio ? profile.bio.trim().split(/\s+/) : []
  const wordCount = bioWords.length
  const shouldTruncate = wordCount > 15
  const truncatedBio = shouldTruncate
    ? `${bioWords.slice(0, 15).join(' ')}... `
    : profile?.bio
  const linkedinHandle = normalizeHandle(
    getFieldValue('linkedin_usr', 'linkedin'),
    'linkedin'
  )
  const instagramHandle = normalizeHandle(
    getFieldValue('insta_usr', 'instagram_usr'),
    'instagram'
  )
  const facebookHandle = normalizeHandle(getFieldValue('facebook_usr'), 'facebook')
  const twitterHandle = normalizeHandle(getFieldValue('twitter_usr', 'x_usr'), 'twitter')
  const youtubeUrl = getFieldValue('youtube', 'youtube_url')
  const tikTokHandle = normalizeHandle(getFieldValue('tiktok_usr', 'tiktok'), 'tiktok')
  const websiteUrl = normalizeWebsiteUrl(getFieldValue('website_url', 'website'))

  const hasSocialLinks = Boolean(
    linkedinHandle ||
      instagramHandle ||
      facebookHandle ||
      twitterHandle ||
      youtubeUrl ||
      tikTokHandle ||
      websiteUrl
  )

  const info = [
    { title: 'Secure and Private', icon: LockIcon },
    { title: 'HIPPA compliant', icon: ClockIcon },
  ]

  return (
    <div className="profile_sidebar">
      <div className="profile_sidebar__head">
        {profile?.photo ? (
          <div className="profile_sidebar_img profile_sidebar__img">
            <img
              className="profile_sidebar_img__in"
              src={profile?.photo}
              alt={profile?.user?.name}
            />
          </div>
        ) : (
          <div className="profile_defualt_avatar">
            {profile?.user?.name?.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="profile_sidebar__head_text">
          <div className="profile_sidebar_name profile_sidebar__name">
            {profile?.user?.name}
          </div>
          <div className="profile_sidebar_profession profile_sidebar__profession">
            {profile?.category}
          </div>
        </div>
      </div>
      <div className="profile_sidebar_description profile_sidebar__description">
        {shouldTruncate ? truncatedBio : profile?.bio}
        {shouldTruncate && (
          <button
            className="profile_sidebar__read_more"
            onClick={() => setIsBioDrawerOpen(true)}
          >
            read more
          </button>
        )}
      </div>

      {isBioDrawerOpen && (
        <div className="profile_bio_drawer" onClick={() => setIsBioDrawerOpen(false)}>
          <div className="profile_bio_drawer__panel" onClick={(e) => e.stopPropagation()}>
            <div className="profile_bio_drawer__content">
              <div className="profile_bio_drawer__header">
                <h2 className="profile_bio_drawer__title">{profile?.user?.name}'s Bio</h2>
                <button
                  className="profile_bio_drawer__close"
                  onClick={() => setIsBioDrawerOpen(false)}
                  aria-label="Close bio panel"
                >
                  ✕
                </button>
              </div>
              <p className="profile_bio_drawer__text">{profile?.bio}</p>
            </div>
          </div>
        </div>
      )}

      <div className="profile_sidebar_salaries_wrap">
        {hasSocialLinks && (
          <div className="profile_sidebar_social profile_sidebar__social">
            <ul className="profile_sidebar_social__list">
              {linkedinHandle && (
                <li className="profile_sidebar_social__item" key={0}>
                  <a
                    className="profile_sidebar_social__item_link"
                    href={`https://linkedin.com/in/${linkedinHandle}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="profile_sidebar_social__item_icon">
                      <LinkedInIcon className="icon size_mod" />
                    </div>
                  </a>
                </li>
              )}
              {instagramHandle && (
                <li className="profile_sidebar_social__item" key={1}>
                  <a
                    className="profile_sidebar_social__item_link"
                    href={`https://instagram.com/${instagramHandle}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="profile_sidebar_social__item_icon">
                      <InstagramIcon className="icon size_mod" />
                    </div>
                  </a>
                </li>
              )}
              {facebookHandle && (
                <li className="profile_sidebar_social__item" key={2}>
                  <a
                    className="profile_sidebar_social__item_link"
                    href={`https://facebook.com/${facebookHandle}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="profile_sidebar_social__item_icon">
                      <FacebookIcon className="icon size_mod" />
                    </div>
                  </a>
                </li>
              )}
              {twitterHandle && (
                <li className="profile_sidebar_social__item" key={3}>
                  <a
                    className="profile_sidebar_social__item_link"
                    href={`https://twitter.com/${twitterHandle}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="profile_sidebar_social__item_icon">
                      <TwitterIcon className="icon size_mod" />
                    </div>
                  </a>
                </li>
              )}
              {youtubeUrl && (
                <li className="profile_sidebar_social__item" key={4}>
                  <a
                    className="profile_sidebar_social__item_link"
                    href={normalizeWebsiteUrl(youtubeUrl)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="profile_sidebar_social__item_icon">
                      <YoutubeIcon className="icon size_mod" />
                    </div>
                  </a>
                </li>
              )}
              {tikTokHandle && (
                <li className="profile_sidebar_social__item" key={5}>
                  <a
                    className="profile_sidebar_social__item_link"
                    href={`https://tiktok.com/@${tikTokHandle}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="profile_sidebar_social__item_icon">
                      <TikTokIcon className="icon size_mod" />
                    </div>
                  </a>
                </li>
              )}
              {websiteUrl && (
                <li className="profile_sidebar_social__item" key={6}>
                  <a
                    className="profile_sidebar_social__item_link"
                    href={websiteUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="profile_sidebar_social__item_text">
                      {domainFromUrl(websiteUrl)}
                    </div>
                  </a>
                </li>
              )}
            </ul>
          </div>
        )}
        {!isNaN(parseInt(eventEntryFee)) && parseInt(eventEntryFee) !== 0 && (
          <ul className="profile_sidebar_salaries profile_sidebar__salaries">
            <li className="profile_sidebar_salaries__item">
              <span className="profile_sidebar_salaries__item_money">
                ${parseInt(eventEntryFee).toFixed(0)}
                <span className="profile_sidebar_salaries__item_money_separator">/</span>
              </span>
              <span className="profile_sidebar_salaries__item_period">
                {sessionTypes[profile?.category]}
              </span>
            </li>
          </ul>
        )}
        <ul className="profile_sidebar__info">
          {info.map(({ title, icon: Icon }, index) => (
            <div className="profile_sidebar__info_item" key={index}>
              <div className="profile_sidebar__info_icon">
                {Icon && <Icon className="icon size_mod" />}
              </div>
              {title}
            </div>
          ))}
        </ul>
      </div>
    </div>
  )
}

export { ProfileSideBar }
